import { Injectable } from '@angular/core';
import { addressStringShort } from '@core/util/address';
import { Business } from '@models/businesses';
import { BusinessClient, ClientType, PersonClient } from '@models/clients';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from './base-form-mapper';
import { NameFormattingSettings } from '@models/businessSettings/settings';

@Injectable({
	providedIn: 'root',
})
export class ON_GST_190_APPENDIX_A_Mapper extends BaseFormMapper<ON_GST_190_APPENDIX_A> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: ON_GST_190_APPENDIX_A,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
	) {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as ON_GST_190_APPENDIX_A);

		let primary = buyers?.find((x) => x.primaryContact);
		if (!primary && buyers?.length > 0) {
			primary = buyers[0];
		}

		if (
			(!_formData?.claimantName || _formData?.claimantName?.length === 0) &&
			primary
		) {
			_formData.claimantName = this.getClientsName(primary);
		}

		if (
			!_formData?.propertyUnitStreet ||
			_formData?.propertyUnitStreet?.length === 0
		) {
			_formData.propertyUnitStreet = addressStringShort(
				matter?.propertyDetails?.address,
			);
		}

		if (
			!_formData?.propertyUnitStreet ||
			_formData?.propertyUnitStreet?.length === 0
		) {
			_formData.propertyUnitStreet = addressStringShort(
				matter?.propertyDetails?.address,
			);
		}

		if (!_formData?.propertyCity || _formData?.propertyCity?.length === 0) {
			_formData.propertyCity = matter?.propertyDetails?.address?.city;
		}

		if (
			(!_formData?.propertyProvince ||
				_formData?.propertyProvince?.length === 0) &&
			matter?.propertyDetails?.address?.province
		) {
			_formData.propertyProvince = matter?.propertyDetails?.address?.province;
		}

		if (
			!_formData?.propertyPostalCode ||
			_formData?.propertyPostalCode?.length === 0
		) {
			_formData.propertyPostalCode =
				matter?.propertyDetails?.address?.postcode?.replace(/ /g, '');
		}

		if (
			(!_formData?.repName || _formData?.repName?.length === 0) &&
			matter?.otherPartyLawFirm?.lawyer
		) {
			_formData.repName = `${matter?.otherPartyLawFirm?.lawyer?.firstName} ${matter?.otherPartyLawFirm?.lawyer?.lastName}`;
		}

		if (
			(!_formData?.repFirmName || _formData?.repFirmName?.length === 0) &&
			matter?.otherPartyLawFirm?.name
		) {
			_formData.repFirmName = matter?.otherPartyLawFirm?.name;
		}

		if (
			(!_formData?.repAddressUnitStreet ||
				_formData?.repAddressUnitStreet?.length === 0) &&
			matter?.otherPartyLawFirm?.address
		) {
			_formData.repAddressUnitStreet = addressStringShort(
				matter?.otherPartyLawFirm?.address,
			);
		}

		if (
			(!_formData?.repCity || _formData?.repCity?.length === 0) &&
			matter?.otherPartyLawFirm?.address
		) {
			_formData.repCity = matter?.otherPartyLawFirm?.address?.city;
		}

		if (
			(!_formData?.repProvince || _formData?.repProvince?.length === 0) &&
			matter?.otherPartyLawFirm?.address?.province
		) {
			_formData.repProvince = matter?.otherPartyLawFirm?.address?.province;
		}

		if (
			(!_formData?.repPostalCode || _formData?.repPostalCode?.length === 0) &&
			matter?.otherPartyLawFirm?.address?.postcode
		) {
			_formData.repPostalCode =
				matter?.otherPartyLawFirm?.address?.postcode?.replace(/ /g, '');
		}

		if (
			(!_formData?.repPhoneNumber || _formData?.repPhoneNumber?.length === 0) &&
			matter?.otherPartyLawFirm?.phoneNumber
		) {
			_formData.repPhoneNumber = matter?.otherPartyLawFirm?.phoneNumber;
		}

		if (
			(!_formData?.repFaxNumber || _formData?.repFaxNumber?.length === 0) &&
			matter?.otherPartyLawFirm?.faxNumber
		) {
			_formData.repFaxNumber = matter?.otherPartyLawFirm?.faxNumber;
		}

		if (
			(!_formData?.repFirmName || _formData?.repFirmName?.length === 0) &&
			matter?.otherPartyLawFirm?.name
		) {
			_formData.repFirmName = matter?.otherPartyLawFirm?.name;
		}

		if (!_formData?.date || _formData?.date?.length === 0) {
			_formData.date = format(new Date(), 'yyyyMMdd');
		}

		return _formData;
	}

	MapValues(): Partial<ON_GST_190_APPENDIX_A> {
		return {};
	}
}

const Mappings: ON_GST_190_APPENDIX_A = {
	claimantName: 'form1[0].Page1[0].ClaimantInformation[0].Name[0].Name[0]',
	propertyUnitStreet:
		'form1[0].Page1[0].ClaimantInformation[0].Address[0].Address[0]',
	propertyCity: 'form1[0].Page1[0].ClaimantInformation[0].Address[0].City[0]',
	propertyProvince:
		'form1[0].Page1[0].ClaimantInformation[0].Address[0].ProvinceTerritory_DropDown[0]',
	propertyPostalCode:
		'form1[0].Page1[0].ClaimantInformation[0].Address[0].Canadian_PostalCode[0].PostalCode[0]',
	repName: 'form1[0].Page2[0].RepInformation[0].Name[0].Name[0]',
	repFirmName: 'form1[0].Page2[0].RepInformation[0].FirmName[0].Name[0]',
	repAddressUnitStreet:
		'form1[0].Page2[0].RepInformation[0].MailingAddress[0].MailingAddress[0]',
	repCity: 'form1[0].Page2[0].RepInformation[0].MailingAddress[0].City[0]',
	repProvince:
		'form1[0].Page2[0].RepInformation[0].MailingAddress[0].ProvinceTerritory_DropDown[0]',
	repPostalCode:
		'form1[0].Page2[0].RepInformation[0].MailingAddress[0].Canadian_PostalCode[0].PostalCode[0]',
	repPhoneNumber:
		'form1[0].Page2[0].RepInformation[0].MailingAddress[0].CanadianPhoneNumber[0].CanadianPhoneNumber[0]',
	repPhoneNumberExt:
		'form1[0].Page2[0].RepInformation[0].MailingAddress[0].CanadianPhoneNumber[0].Extension[0]',
	repFaxNumber:
		'form1[0].Page2[0].RepInformation[0].MailingAddress[0].CanadianFaxNumber[0]',
	date: 'form1[0].Page2[0].RepInformation[0].Date[0].Date[0].DateYYYYMMDD_Comb[0]',
	repCertification: 'form1[0].Page2[0].RepInformation[0].Rep_CheckBox[0]',
};

export interface ON_GST_190_APPENDIX_A {
	claimantName: string;
	propertyUnitStreet: string;
	propertyCity: string;
	propertyProvince: string;
	propertyPostalCode: string;
	repName: string;
	repFirmName: string;
	repAddressUnitStreet: string;
	repCity: string;
	repProvince: string;
	repPostalCode: string;
	repPhoneNumber: string;
	repPhoneNumberExt: string;
	repFaxNumber: string;
	date: string;
	repCertification: string;
}
